// =====================================================
// FavoritesContext.jsx   unified save + email OTP sign-in
//
// Business and community favorites now use ONE sign-in flow:
//   Save prompt -> email -> 6-digit code -> signed in
//
// The legacy /SignInModal username/password route is NOT used
// by favorites anymore.
// =====================================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { FaArrowLeft, FaHeart, FaTimes } from "react-icons/fa";

import {
  api,
  useSession,
  setSession,
} from "./session";

import "./Favorites.css";
import "./FavoritesAuth.css";

const PENDING_SAVE_KEY = "gohubPendingSave";

const FavoritesContext = createContext(null);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getApiErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;

  return (
    data?.message ||
    data?.body?.message ||
    (typeof data === "string" ? data : "") ||
    error?.message ||
    fallbackMessage
  );
}

function capitalizeFirstName(value) {
  const clean = String(value || "").trim();

  if (!clean) {
    return "";
  }

  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);

  if (!ctx) {
    throw new Error(
      "useFavorites must be used inside <FavoritesProvider>"
    );
  }

  return ctx;
}

export function FavoritesProvider({ children }) {
  const user = useSession();

  const [savedIds, setSavedIds] = useState(() => new Set());

  /*
   * promptFor examples:
   *
   * business:
   * {
   *   kind: "business",
   *   businessId,
   *   businessName,
   *   name
   * }
   *
   * community:
   * {
   *   kind: "community",
   *   name,
   *   onCancel
   * }
   */
  const [promptFor, setPromptFor] = useState(null);

  const [authStage, setAuthStage] = useState("prompt");
  const [authEmail, setAuthEmail] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const showToast = useCallback((message) => {
    setToast(message);

    clearTimeout(toastTimer.current);

    toastTimer.current = setTimeout(
      () => setToast(""),
      2600
    );
  }, []);

  useEffect(
    () => () => clearTimeout(toastTimer.current),
    []
  );

  /* ---------- load saved business ids ---------- */

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setSavedIds(new Set());
      return undefined;
    }

    api
      .get("/api/favorites/ids")
      .then((response) => {
        if (cancelled) {
          return;
        }

        const ids = response?.data?.ids || [];

        setSavedIds(
          new Set(ids.map(String))
        );
      })
      .catch(() => {
        /* backend unreachable   hearts just start empty */
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  /* ---------- shared sign-in prompt ---------- */

  const resetAuthFields = useCallback(() => {
    setAuthStage("prompt");
    setAuthEmail("");
    setChallengeId("");
    setVerificationCode("");
    setAuthBusy(false);
    setAuthError("");
  }, []);

  const requestSignInPrompt = useCallback(
    ({
      kind = "place",
      name = "",
      onCancel = null,
      businessId = "",
      businessName = "",
    } = {}) => {
      resetAuthFields();

      setPromptFor({
        kind,
        name,
        onCancel,
        businessId,
        businessName,
      });
    },
    [resetAuthFields]
  );

  const dismissPrompt = useCallback(() => {
    if (promptFor?.kind === "business") {
      localStorage.removeItem(PENDING_SAVE_KEY);
    }

    if (typeof promptFor?.onCancel === "function") {
      promptFor.onCancel();
    }

    setPromptFor(null);
    resetAuthFields();
  }, [promptFor, resetAuthFields]);

  /* ---------- business favorite toggle ---------- */

  const applyToggleLocally = useCallback((businessId) => {
    setSavedIds((prev) => {
      const next = new Set(prev);

      if (next.has(businessId)) {
        next.delete(businessId);
      } else {
        next.add(businessId);
      }

      return next;
    });
  }, []);

  const toggleSave = useCallback(
    async (rawBusinessId, businessName = "") => {
      const businessId = String(rawBusinessId);

      if (!user) {
        localStorage.setItem(
          PENDING_SAVE_KEY,
          JSON.stringify({
            businessId,
            businessName,
          })
        );

        requestSignInPrompt({
          kind: "business",
          name: businessName,
          businessId,
          businessName,
        });

        return;
      }

      const wasSaved =
        savedIds.has(businessId);

      applyToggleLocally(businessId);

      try {
        const response = await api.post(
          "/api/favorites/toggle",
          {
            businessId,
          }
        );

        const saved = Boolean(
          response?.data?.saved
        );

        showToast(
          saved
            ? `Saved ${
                businessName ||
                "to your places"
              } ♥`
            : "Removed from saved"
        );
      } catch {
        applyToggleLocally(businessId);

        showToast(
          wasSaved
            ? "Could not remove   try again"
            : "Could not save   try again"
        );
      }
    },
    [
      user,
      savedIds,
      applyToggleLocally,
      showToast,
      requestSignInPrompt,
    ]
  );

  /* ---------- complete pending business save after sign-in ---------- */

  useEffect(() => {
    if (!user) {
      return;
    }

    const raw =
      localStorage.getItem(
        PENDING_SAVE_KEY
      );

    if (!raw) {
      return;
    }

    localStorage.removeItem(
      PENDING_SAVE_KEY
    );

    setPromptFor(null);

    try {
      const pending = JSON.parse(raw);

      if (pending?.businessId) {
        toggleSave(
          pending.businessId,
          pending.businessName
        );
      }
    } catch {
      /* ignore malformed pending data */
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isSaved = useCallback(
    (businessId) =>
      savedIds.has(
        String(businessId)
      ),
    [savedIds]
  );

  /* =========================================================
     EMAIL OTP SIGN-IN
     ========================================================= */

  const beginEmailSignIn = () => {
    setAuthError("");
    setAuthStage("email");
  };

  const goBackToPrompt = () => {
    setAuthError("");
    setVerificationCode("");
    setChallengeId("");
    setAuthStage("prompt");
  };

  const sendVerificationCode = async (event) => {
    event?.preventDefault?.();

    setAuthError("");

    const email = authEmail
      .trim()
      .toLowerCase();

    if (!EMAIL_PATTERN.test(email)) {
      setAuthError(
        "Enter a valid email address."
      );
      return;
    }

    setAuthBusy(true);

    try {
      /*
       * Use the same known-good email challenge endpoint
       * already used by AccountGate / Add Business.
       */
      const response = await api.post(
        "/api/auth/email/start",
        {
          email,
          purpose: "partner-signup",
        }
      );

      const returnedChallengeId =
        response?.data?.challengeId;

      if (!returnedChallengeId) {
        throw new Error(
          "The server did not return an email challenge ID."
        );
      }

      setChallengeId(
        returnedChallengeId
      );

      setVerificationCode("");
      setAuthStage("code");
    } catch (error) {
      setAuthError(
        getApiErrorMessage(
          error,
          "We could not send the verification code. Please try again."
        )
      );
    } finally {
      setAuthBusy(false);
    }
  };

  const verifyEmailCode = async (event) => {
    event?.preventDefault?.();

    setAuthError("");

    const cleanCode =
      verificationCode.replace(
        /\D/g,
        ""
      );

    if (
      !challengeId ||
      cleanCode.length !== 6
    ) {
      setAuthError(
        "Enter the 6-digit verification code."
      );
      return;
    }

    setAuthBusy(true);

    try {
      const response = await api.post(
        "/api/auth/email/verify",
        {
          challengeId,
          code: cleanCode,
        }
      );

      const verificationToken =
  response?.data?.verificationToken;

const sessionToken =
  response?.data?.sessionToken;

if (!verificationToken) {
  throw new Error(
    "The server did not return a verification token."
  );
}

if (!sessionToken) {
  throw new Error(
    "The server did not return a session token."
  );
}
      const returnedAccount =
        response?.data?.account || {};

      const returnedFirstName =
        String(
          response?.data?.firstName ||
            response?.data?.first_name ||
            returnedAccount?.firstName ||
            returnedAccount?.first_name ||
            ""
        ).trim();

      const email = authEmail
        .trim()
        .toLowerCase();

      /*
       * This is the SAME site session read by Header,
       * ProfileHub, Add Business, business favorites,
       * and community favorites.
       */
   setSession({
  email,
  firstName:
    capitalizeFirstName(
      returnedFirstName
    ),
  token:
    sessionToken,
});

      setPromptFor(null);
      resetAuthFields();

      showToast(
        "Signed in successfully"
      );
    } catch (error) {
      setAuthError(
        getApiErrorMessage(
          error,
          "The verification code could not be verified."
        )
      );
    } finally {
      setAuthBusy(false);
    }
  };

  const resendVerificationCode = async () => {
    setVerificationCode("");
    await sendVerificationCode();
  };

  /* ---------- context ---------- */

  const value = {
    user,
    isSaved,
    toggleSave,
    savedCount: savedIds.size,
    showToast,

    /*
     * Community hearts use this so they get the EXACT
     * same signup-intent popup as business hearts.
     */
    requestSignInPrompt,
  };

  const isCommunityPrompt =
    promptFor?.kind === "community";

  const isEventPrompt =
    promptFor?.kind === "event";

  const isEventSavePrompt =
    promptFor?.kind === "event-save";

  const promptTitle = isEventSavePrompt
    ? "Save events you love"
    : isEventPrompt
    ? "Post events to your community"
    : isCommunityPrompt
      ? "Save communities you love"
      : "Save places you love";

  const promptName =
    promptFor?.name ||
    promptFor?.businessName ||
    "";

  return (
    <FavoritesContext.Provider value={value}>
      {children}

      {/* =====================================================
          UNIFIED SAVE / SIGN-IN MODAL
          ===================================================== */}

      {promptFor && (
        <div
          className="ghfav-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ghfav-title"
        >
          <div className="ghfav-modal">
            <button
              type="button"
              className="ghfav-close"
              onClick={dismissPrompt}
              aria-label="Close"
            >
              <FaTimes
                aria-hidden="true"
              />
            </button>

            <div
              className="ghfav-modal-icon"
              aria-hidden="true"
            >
              <FaHeart />
            </div>

            {/* ---------- FIRST SCREEN ---------- */}

            {authStage ===
              "prompt" && (
              <>
                <h2 id="ghfav-title">
                  {promptTitle}
                </h2>

                {isEventPrompt ? (
                  <p>
                      Sign in with your email to post events and updates
                      {promptName ? (
                        <>
                          {" "}for the <strong>{promptName}</strong> community
                        </>
                      ) : (
                        <> for your community</>
                      )}
                      . Free, no password needed.
                    </p>
                ) : isEventSavePrompt ? (
                  <p>
                      Sign in with your email to keep
                      {promptName ? (
                        <>
                          {" "}<strong>{promptName}</strong> and other
                        </>
                      ) : (
                        <> your favorite</>
                      )}
                      {" "}community events in one place. Free, no
                      password needed.
                    </p>
                ) : (
                  <p>
                  Sign in with your
                  email to keep
                  {promptName ? (
                    <>
                      {" "}
                      <strong>
                        {promptName}
                      </strong>{" "}
                      and other{" "}
                      {isCommunityPrompt
                        ? "favorite communities"
                        : "favorite local spots"}
                    </>
                  ) : (
                    <>
                      {" "}
                      {isCommunityPrompt
                        ? "your favorite communities"
                        : "a list of your favorite local spots"}
                    </>
                  )}{" "}
                  in one place. Free,
                  no password needed.
                </p>
                )}

                <button
                  type="button"
                  className="ghfav-cta ghfav-cta--primary"
                  onClick={
                    beginEmailSignIn
                  }
                >
                  Sign in / create account
                </button>

                <button
                  type="button"
                  className="ghfav-cta ghfav-cta--text"
                  onClick={
                    dismissPrompt
                  }
                >
                  Not now
                </button>
              </>
            )}

            {/* ---------- EMAIL SCREEN ---------- */}

            {authStage ===
              "email" && (
              <>
                <button
                  type="button"
                  className="ghfav-auth-back"
                  onClick={
                    goBackToPrompt
                  }
                  aria-label="Go back"
                >
                  <FaArrowLeft
                    aria-hidden="true"
                  />
                  Back
                </button>

                <h2 id="ghfav-title">
                  Continue with email
                </h2>

                <p>
                  We&rsquo;ll send you a
                  6-digit verification
                  code. No password
                  needed.
                </p>

                <form
                  className="ghfav-auth-form"
                  onSubmit={
                    sendVerificationCode
                  }
                >
                  <label
                    htmlFor="ghfav-email"
                  >
                    Email address
                  </label>

                  <input
                    id="ghfav-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={authEmail}
                    onChange={(event) => {
                      setAuthEmail(
                        event.target.value
                      );
                      setAuthError("");
                    }}
                    placeholder="you@example.com"
                    disabled={authBusy}
                    autoFocus
                  />

                  {authError && (
                    <p
                      className="ghfav-auth-error"
                      role="alert"
                    >
                      {authError}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="ghfav-cta ghfav-cta--primary"
                    disabled={authBusy}
                  >
                    {authBusy
                      ? "Sending code…"
                      : "Email me a code"}
                  </button>
                </form>

                <button
                  type="button"
                  className="ghfav-cta ghfav-cta--text"
                  onClick={
                    dismissPrompt
                  }
                  disabled={authBusy}
                >
                  Not now
                </button>
              </>
            )}

            {/* ---------- CODE SCREEN ---------- */}

            {authStage ===
              "code" && (
              <>
                <button
                  type="button"
                  className="ghfav-auth-back"
                  onClick={() =>
                    setAuthStage(
                      "email"
                    )
                  }
                  aria-label="Go back"
                >
                  <FaArrowLeft
                    aria-hidden="true"
                  />
                  Back
                </button>

                <h2 id="ghfav-title">
                  Check your email
                </h2>

                <p>
                  Enter the 6-digit code
                  sent to{" "}
                  <strong>
                    {authEmail
                      .trim()
                      .toLowerCase()}
                  </strong>
                  .
                </p>

                <form
                  className="ghfav-auth-form"
                  onSubmit={
                    verifyEmailCode
                  }
                >
                  <label
                    htmlFor="ghfav-code"
                  >
                    Verification code
                  </label>

                  <input
                    id="ghfav-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={
                      verificationCode
                    }
                    onChange={(event) => {
                      setVerificationCode(
                        event.target.value
                          .replace(
                            /\D/g,
                            ""
                          )
                          .slice(0, 6)
                      );
                      setAuthError("");
                    }}
                    placeholder="000000"
                    disabled={authBusy}
                    autoFocus
                  />

                  {authError && (
                    <p
                      className="ghfav-auth-error"
                      role="alert"
                    >
                      {authError}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="ghfav-cta ghfav-cta--primary"
                    disabled={
                      authBusy ||
                      verificationCode
                        .length !== 6
                    }
                  >
                    {authBusy
                      ? "Verifying…"
                      : "Verify and continue"}
                  </button>
                </form>

                <button
                  type="button"
                  className="ghfav-cta ghfav-cta--text"
                  onClick={
                    resendVerificationCode
                  }
                  disabled={authBusy}
                >
                  Resend code
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- toast ---------- */}

      {toast && (
        <div
          className="ghfav-toast"
          role="status"
        >
          {toast}
        </div>
      )}
    </FavoritesContext.Provider>
  );
}
